import { useCallback, useMemo, useState } from 'react';
import {
  makeStyles,
  tokens,
  Text,
  Spinner,
  Button,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  Toast,
  ToastTitle,
  ToastBody,
  useToastController,
  Toaster,
  useId,
  Tooltip,
} from '@coreai/fluentui-react';
import {
  ArrowUploadRegular,
  OpenRegular,
  Flash20Filled,
  Pause20Regular,
  CopyRegular,
  CheckmarkRegular,
} from '@fluentui/react-icons';
import { InputOutputDetail, type DetailProperty } from '../InputOutputDetail/InputOutputDetail';
import { RunStatusBadge } from '../RunsSidebar/RunStatusBadge';
import {
  useTriggerHistoryRecord,
  useTriggerHistoryContent,
  useResubmitTriggerHistory,
} from '@/hooks/useTriggerHistory';
import { useMonitoringStore } from '@/stores/monitoringStore';
import { formatDateTime, formatDuration } from '@/utils/dateTime';
import { formatBytes } from '@/utils/primitives';

const sanitizeDownloadNamePart = (value: string) => value.replace(/[\\/:*?"<>|]/g, '-');

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    overflow: 'hidden',
    backgroundColor: tokens.colorNeutralBackground1,
  },
  empty: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    gap: tokens.spacingVerticalS,
    color: tokens.colorNeutralForeground3,
    padding: tokens.spacingVerticalXL,
  },
  header: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: tokens.spacingHorizontalS,
    padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalL}`,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    flexShrink: 0,
    minWidth: 0,
  },
  headerTitleBlock: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'baseline',
    gap: tokens.spacingHorizontalS,
    minWidth: 0,
    flex: 1,
  },
  headerTitle: {
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase400,
    lineHeight: tokens.lineHeightBase400,
    color: tokens.colorNeutralForeground1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    minWidth: 0,
    margin: 0,
  },
  headerIdRow: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXXS,
    minWidth: 0,
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
    lineHeight: tokens.lineHeightBase200,
    // Reveal the copy button only on hover/focus-within so the row stays
    // visually quiet at rest. Keyboard users still get it because the
    // button takes focus on Tab.
    '& [data-id-copy]': {
      opacity: 0,
      transitionProperty: 'opacity',
      transitionDuration: tokens.durationNormal,
      transitionTimingFunction: tokens.curveEasyEase,
    },
    ':hover [data-id-copy]': {
      opacity: 1,
    },
    ':focus-within [data-id-copy]': {
      opacity: 1,
    },
    '@media (prefers-reduced-motion: reduce)': {
      '& [data-id-copy]': {
        transitionDuration: '0.01ms',
      },
    },
  },
  headerIdValue: {
    fontSize: tokens.fontSizeBase100,
    lineHeight: tokens.lineHeightBase100,
    fontWeight: tokens.fontWeightRegular,
    color: tokens.colorNeutralForeground3,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    minWidth: 0,
    maxWidth: '100%',
  },
  headerCopyBtn: {
    minWidth: 'auto',
    height: '20px',
    paddingBlock: 0,
    paddingInline: tokens.spacingHorizontalXS,
  },
  headerRight: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    flexShrink: 0,
    // Align with the title row, not the ID subline.
    marginTop: tokens.spacingVerticalXXS,
  },
  badges: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    flexShrink: 0,
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    flexShrink: 0,
  },
  firedBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXXS,
    padding: `${tokens.spacingVerticalXXS} ${tokens.spacingHorizontalS}`,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorBrandBackground2,
    color: tokens.colorBrandForeground2,
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    lineHeight: tokens.lineHeightBase200,
  },
  skippedBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXXS,
    padding: `${tokens.spacingVerticalXXS} ${tokens.spacingHorizontalS}`,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground3,
    color: tokens.colorNeutralForeground2,
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    lineHeight: tokens.lineHeightBase200,
  },
  // Body wraps the InputOutputDetail so the surrounding panel can flow
  // header → optional error banner → loader → side-by-side detail without
  // the detail's own flex sizing fighting the panel container.
  body: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    overflow: 'hidden',
  },
  loadingState: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: tokens.spacingHorizontalS,
    flex: 1,
    color: tokens.colorNeutralForeground3,
  },
  errorBanner: {
    flexShrink: 0,
    margin: tokens.spacingHorizontalM,
  },
  statusSucceeded: { color: tokens.colorPaletteGreenForeground1 },
  statusFailed: { color: tokens.colorPaletteRedForeground1 },
  statusSkipped: { color: tokens.colorNeutralForeground3 },
});

type TriggerHistoryDetailPanelProps = {
  /**
   * Callback invoked when the user wants to view the run produced by a
   * fired trigger. Receives the run's name. Implementer should switch the
   * monitoring mode back to "runs" and select that run.
   */
  onViewRun?: (runName: string) => void;
};

/**
 * Detail surface for a single trigger evaluation. Reuses the shared
 * `InputOutputDetail` layout (side-by-side JsonViewers + property strip)
 * so trigger evaluations and action runs share visual language and
 * affordances (search, formatted/raw, copy, download).
 */
export const TriggerHistoryDetailPanel = ({ onViewRun }: TriggerHistoryDetailPanelProps) => {
  const styles = useStyles();
  const toasterId = useId('trigger-history-toaster');
  const { dispatchToast } = useToastController(toasterId);

  const triggerName = useMonitoringStore((s) => s.selectedTriggerName);
  const historyName = useMonitoringStore((s) => s.selectedTriggerHistoryId);

  const { data: history, isLoading: historyLoading, error: historyError } =
    useTriggerHistoryRecord(triggerName, historyName);
  const { data: content, isLoading: contentLoading } = useTriggerHistoryContent(
    triggerName,
    historyName
  );

  const resubmit = useResubmitTriggerHistory();
  const [isResubmitting, setIsResubmitting] = useState(false);
  const [idCopied, setIdCopied] = useState(false);

  const handleCopyId = useCallback(async () => {
    if (!history?.name) return;
    try {
      await navigator.clipboard.writeText(history.name);
      setIdCopied(true);
      window.setTimeout(() => setIdCopied(false), 1500);
    } catch {
      // Clipboard may be unavailable; fail silently.
    }
  }, [history?.name]);

  const handleResubmit = useCallback(async () => {
    if (!triggerName || !historyName) return;
    setIsResubmitting(true);
    const result = await resubmit.resubmit({ triggerName, historyName });
    setIsResubmitting(false);
    if (result.success) {
      dispatchToast(
        <Toast>
          <ToastTitle>Trigger resubmitted</ToastTitle>
          <ToastBody>A new run is being queued.</ToastBody>
        </Toast>,
        { intent: 'success' }
      );
    } else {
      dispatchToast(
        <Toast>
          <ToastTitle>Resubmit failed</ToastTitle>
          <ToastBody>{result.error}</ToastBody>
        </Toast>,
        { intent: 'error' }
      );
    }
  }, [triggerName, historyName, resubmit, dispatchToast]);

  // Wall-clock duration so we can show "1m 5s" instead of forcing the
  // user to do timestamp math on twin "Jun 3, 07:46 PM" rows.
  const durationMs = useMemo(() => {
    if (!history?.startTime || !history?.endTime) return undefined;
    const start = Date.parse(history.startTime);
    const end = Date.parse(history.endTime);
    if (!Number.isFinite(start) || !Number.isFinite(end)) return undefined;
    return Math.max(0, end - start);
  }, [history?.startTime, history?.endTime]);

  // contentSize ships with the link metadata, so we can render the
  // payload size next to the pane label even before the JSON streams in.
  const inputSize = formatBytes(history?.inputsLink?.contentSize);
  const outputSize = formatBytes(history?.outputsLink?.contentSize);

  const statusClass = useMemo(() => {
    if (history?.status === 'succeeded') return styles.statusSucceeded;
    if (history?.status === 'failed') return styles.statusFailed;
    if (history?.status === 'skipped') return styles.statusSkipped;
    return undefined;
  }, [history?.status, styles]);

  const properties = useMemo<DetailProperty[]>(() => {
    if (!history) return [];
    const cells: DetailProperty[] = [
      {
        label: 'Status',
        value: history.status.charAt(0).toUpperCase() + history.status.slice(1),
        ...(statusClass ? { valueClassName: statusClass } : {}),
      },
      {
        label: 'Fired',
        value: history.fired ? 'True' : 'False',
      },
    ];
    if (history.startTime) {
      cells.push({
        label: 'Started',
        value: formatDateTime(history.startTime, 'short'),
        title: formatDateTime(history.startTime),
      });
    }
    if (history.endTime) {
      cells.push({
        label: 'Ended',
        value: formatDateTime(history.endTime, 'short'),
        title: formatDateTime(history.endTime),
      });
    }
    if (durationMs !== undefined) {
      cells.push({
        label: 'Duration',
        value: formatDuration(durationMs),
        title: `${durationMs} ms`,
      });
    }
    if (history.code) {
      cells.push({
        label: 'Code',
        value: history.code,
      });
    }
    if (history.clientTrackingId) {
      cells.push({
        label: 'Tracking ID',
        value: history.clientTrackingId,
        title: history.clientTrackingId,
        copyable: true,
        copyValue: history.clientTrackingId,
      });
    }
    return cells;
  }, [history, durationMs, statusClass]);

  if (!triggerName || !historyName) {
    return (
      <div className={styles.empty}>
        <Text>Select a trigger evaluation to see its details.</Text>
      </div>
    );
  }

  if (historyLoading && !history) {
    return (
      <div className={styles.empty}>
        <Spinner size="small" label="Loading trigger history…" />
      </div>
    );
  }

  if (historyError || !history) {
    return (
      <div className={styles.empty}>
        <MessageBar intent="error" layout="multiline">
          <MessageBarBody>
            <MessageBarTitle>Could not load trigger history</MessageBarTitle>
            {historyError instanceof Error ? historyError.message : 'Unknown error'}
          </MessageBarBody>
        </MessageBar>
      </div>
    );
  }

  // `null` is a legitimate JSON value, so distinguish "still loading" /
  // "no link" (undefined) from "the server returned literal null".
  // Use the resolved content when present, otherwise undefined; the
  // InputOutputDetail's JsonViewer then renders the empty-state itself.
  const inputData = content?.input;
  const outputData = content?.output;
  const triggerDownloadStem = [
    'trigger',
    sanitizeDownloadNamePart(triggerName),
    sanitizeDownloadNamePart(history.name),
  ].join('-');

  return (
    <div className={styles.container}>
      <Toaster toasterId={toasterId} />
      <div className={styles.header}>
        <div className={styles.headerTitleBlock}>
          <Tooltip content={triggerName} relationship="label" withArrow>
            <Text className={styles.headerTitle} as="h2" truncate wrap={false}>
              {triggerName}
            </Text>
          </Tooltip>
          <div className={styles.headerIdRow}>
            <span className={styles.headerIdValue}>{history.name}</span>
            <Tooltip
              content={idCopied ? 'Copied' : 'Copy ID'}
              relationship="label"
              withArrow
            >
              <Button
                data-id-copy
                aria-label="Copy ID"
                appearance="subtle"
                size="small"
                className={styles.headerCopyBtn}
                icon={idCopied ? <CheckmarkRegular /> : <CopyRegular />}
                onClick={handleCopyId}
              />
            </Tooltip>
          </div>
        </div>
        <div className={styles.headerRight}>
          <span className={styles.badges}>
            <RunStatusBadge status={history.status} size="small" />
            {history.fired ? (
              <span className={styles.firedBadge}>
                <Flash20Filled aria-hidden="true" /> Fired
              </span>
            ) : (
              <span className={styles.skippedBadge}>
                <Pause20Regular aria-hidden="true" /> Did not fire
              </span>
            )}
          </span>
          <div className={styles.actions}>
            {history.fired && history.run?.name && onViewRun && (
              <Button
                appearance="subtle"
                size="small"
                icon={<OpenRegular />}
                onClick={() => onViewRun(history.run!.name)}
              >
                View run
              </Button>
            )}
            <Button
              appearance="secondary"
              size="small"
              icon={<ArrowUploadRegular />}
              onClick={handleResubmit}
              disabled={isResubmitting}
            >
              {isResubmitting ? 'Resubmitting…' : 'Resubmit'}
            </Button>
          </div>
        </div>
      </div>
      {history.error && (
        <MessageBar intent="error" layout="multiline" className={styles.errorBanner}>
          <MessageBarBody>
            <MessageBarTitle>{history.error.code ?? 'Error'}</MessageBarTitle>
            {history.error.message}
          </MessageBarBody>
        </MessageBar>
      )}
      <div className={styles.body}>
        {contentLoading && !content ? (
          <div className={styles.loadingState}>
            <Spinner size="small" />
            <Text>Loading trigger payload…</Text>
          </div>
        ) : (
          <InputOutputDetail
            input={inputData}
            output={outputData}
            downloadStem={triggerDownloadStem}
            properties={properties}
            inputLabel="Input"
            outputLabel="Output"
            ariaLabel="Trigger evaluation properties"
            {...(inputSize ? { inputSize } : {})}
            {...(outputSize ? { outputSize } : {})}
          />
        )}
      </div>
    </div>
  );
};
